// features/admin/setCreditApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { adminrefreshToken } from '../../utils/adminRefreshToken.ts';

// Updated interfaces based on backend response
export interface UserCreditDetail {
  credit_limit: number;
  credit_balance: number;
}

export interface OrderDetail {
  order_id: number;
  invoice_no: string;
  total_order_amount: number;
  order_status: string;
  pay_later: boolean | number;
}

export interface RepaymentDetail {
  repayment_id: number;
  order_id: number;
  amount: number;
  stripe_session_id: string;
  stripe_payment_intent: string;
  status: string;
  repayment_date: string;
}

export interface CreditUserResponse {
  user_id: number;
  first_name: string;
  last_name: string;
  user_email: string;
  user_contact: string;
  credit: UserCreditDetail;
  orders: OrderDetail[];
  repayments: RepaymentDetail[];
}

export interface CreditApiResponse<T> {
  error: boolean;
  message: string;
  count?: number;
  result?: T;
  data?: T;
  total?: number;
}

export interface SetCreditRequest {
  user_id: number;
  credit_limit: number;
  balance?: number;
}

export interface UpdateCreditRequest {
  user_id: number;
  credit_limit: number;
  balance?: number;
}

export interface DeleteCreditRequest {
  user_id: number;
}

// Frontend Credit User interface (simplified for UI)
export interface CreditUser {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  credit_limit: number;
  balance: number;
  orders_count?: number;
  repayments_count?: number;
  created_at?: string;
  updated_at?: string;
}

// User interface for dropdown list
export interface User {
  user_id: number;
  first_name: string;
  last_name: string;
  user_email: string;
  user_contact: string;
  status?: string;
  member_type?: string;
  subscription_date?: string | null;
  expiry_date?: string | null;
}

// New interfaces for user search
export interface SearchUserRequest {
  search?: string;
  limit?: number;
}

export interface SearchUser {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
}

export const CREDIT_API_CONFIG = {
  BASE_URL: 'https://fftestservice.sarsetech.com',
  ENDPOINTS: {
    SET_CREDIT: '/admin/setCredit', 
    LIST_CREDIT_USERS: '/admin/CreditlistUsers', 
    UPDATE_CREDIT: '/admin/setCredit',
    DELETE_CREDIT: '/admin/deleteCredit',
    GET_USER_CREDIT: '/admin/getUserCredit',
    SEARCH_USERS: '/admin/searchUsers',
    LIST_USERS: '/admin/listUsers', 
  },
};

export const creditSalesApi = createApi({
  reducerPath: 'setCreditApi',
  baseQuery: fetchBaseQuery({
    baseUrl: CREDIT_API_CONFIG.BASE_URL,
    prepareHeaders: async (headers) => {
      // Add authkey
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      
      // Add admin token if exists
      const token = localStorage.getItem('admin_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      // Set content type to application/json for consistency
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['UserCredit', 'CreditUsers', 'SearchUsers', 'AllUsers'],
  endpoints: (build) => ({
    // Set credit for a user (create new credit facility)
    setCredit: build.mutation<CreditApiResponse<any>, SetCreditRequest>({
      query: (data) => {
        const formData = {
          user_id: data.user_id,
          credit_limit: data.credit_limit,
          balance: data.balance || data.credit_limit,
        };
        
        console.log('SetCredit request data:', formData);
        
        return {
          url: CREDIT_API_CONFIG.ENDPOINTS.SET_CREDIT,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['CreditUsers', 'AllUsers'],
      transformResponse: (response: any) => {
        console.log('SetCredit response:', response);
        return {
          error: response.error || false,
          message: response.message || 'Credit set successfully',
          result: response.result || response.data,
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('SetCredit error:', response);
        return {
          error: true,
          message: response.data?.message || response.error || 'Failed to set credit',
          status: response.status,
        };
      },
    }),

    // List all users with credit facilities
    listCreditUsers: build.query<CreditApiResponse<CreditUser[]>, void>({
      query: () => {
        const adminUserId = localStorage.getItem('admin_user_id') || '110';
        const requestData = {
          user_id: parseInt(adminUserId)
        };
        
        console.log('ListCreditUsers request data:', requestData);
        
        return {
          url: CREDIT_API_CONFIG.ENDPOINTS.LIST_CREDIT_USERS,
          method: 'POST',
          body: requestData,
        };
      },
      providesTags: ['CreditUsers'],
      transformResponse: (response: any) => {
        console.log('ListCreditUsers raw response:', response);
        
        if (!response || response.error) {
          return {
            error: true,
            message: response?.message || 'No data received',
            result: [],
            data: [],
          };
        }

        // Transform backend response to frontend format
        const users: CreditUser[] = [];
        
        if (response.data && Array.isArray(response.data)) {
          for (const backendUser of response.data) {
            // Map backend fields to frontend format
            const frontendUser: CreditUser = {
              user_id: backendUser.user_id,
              first_name: backendUser.first_name || '',
              last_name: backendUser.last_name || '',
              email: backendUser.user_email || '',
              phone: backendUser.user_contact || '',
              credit_limit: backendUser.credit?.credit_limit || 0,
              balance: backendUser.credit?.credit_balance || 0,
              orders_count: backendUser.orders?.length || 0,
              repayments_count: backendUser.repayments?.length || 0,
            };
            users.push(frontendUser);
          }
        }
        
        console.log('Transformed users:', users);
        
        return {
          error: response.error || false,
          message: response.message || '',
          count: response.count || users.length,
          result: users,
          data: users,
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('ListCreditUsers error:', response);
        return {
          error: true,
          message: response.data?.message || response.error || 'Failed to load credit users',
          status: response.status,
        };
      },
    }),

    // List all users (for dropdown)
    listAllUsers: build.query<CreditApiResponse<User[]>, { filter_type?: number }>({
      query: (params) => {
        const requestData = {
          filter_type: params?.filter_type ?? 0
        };
        
        console.log('ListAllUsers request data:', requestData);
        
        return {
          url: CREDIT_API_CONFIG.ENDPOINTS.LIST_USERS,
          method: 'POST',
          body: requestData,
        };
      },
      providesTags: ['AllUsers'],
      transformResponse: (response: any) => {
        console.log('ListAllUsers raw response:', response);
        
        let users: User[] = [];
        
        // Handle different response structures
        if (response?.error === false && response?.data && Array.isArray(response.data)) {
          users = response.data;
        } else if (Array.isArray(response)) {
          users = response;
        } else if (response?.data && Array.isArray(response.data)) {
          users = response.data;
        } else if (response?.users && Array.isArray(response.users)) {
          users = response.users;
        }
        
        // Transform users to ensure consistent structure
        const transformedUsers: User[] = users.map((user: any) => ({
          user_id: user.user_id || user.id || 0,
          first_name: user.first_name || user.firstName || '',
          last_name: user.last_name || user.lastName || '',
          user_email: user.user_email || user.email || '',
          user_contact: user.user_contact || user.contact || user.phone || '',
          status: user.status || 'active',
          member_type: user.member_type || user.membership_type || 'none',
          subscription_date: user.subscription_date || null,
          expiry_date: user.expiry_date || null,
        }));
        
        console.log('Transformed all users:', transformedUsers);
        
        return {
          error: response?.error || false,
          message: response?.message || 'Users loaded successfully',
          total: response?.total || response?.count || transformedUsers.length,
          result: transformedUsers,
          data: transformedUsers,
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('ListAllUsers error:', response);
        return {
          error: true,
          message: response.data?.message || response.error || 'Failed to load users',
          data: [],
          result: [],
        };
      },
    }),

    // Update credit limit for existing user
    updateCredit: build.mutation<CreditApiResponse<any>, UpdateCreditRequest>({
      query: (data) => {
        const formData = {
          user_id: data.user_id,
          credit_limit: data.credit_limit,
          balance: data.balance || data.credit_limit,
        };
        
        console.log('UpdateCredit request data:', formData);
        
        return {
          url: CREDIT_API_CONFIG.ENDPOINTS.UPDATE_CREDIT,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['CreditUsers'],
      transformResponse: (response: any) => {
        console.log('UpdateCredit response:', response);
        return {
          error: response.error || false,
          message: response.message || 'Credit updated successfully',
          result: response.result || response.data,
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('UpdateCredit error:', response);
        return {
          error: true,
          message: response.data?.message || response.error || 'Failed to update credit',
          status: response.status,
        };
      },
    }),

    // Delete credit facility for a user
    deleteCredit: build.mutation<CreditApiResponse<any>, DeleteCreditRequest>({
      query: (data) => {
        const formData = {
          user_id: data.user_id,
        };
        
        console.log('DeleteCredit request data:', formData);
        
        return {
          url: CREDIT_API_CONFIG.ENDPOINTS.DELETE_CREDIT,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['CreditUsers', 'AllUsers'],
      transformResponse: (response: any) => {
        console.log('DeleteCredit response:', response);
        return {
          error: response.error || false,
          message: response.message || 'Credit facility removed successfully',
          result: response.result || response.data,
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('DeleteCredit error:', response);
        return {
          error: true,
          message: response.data?.message || response.error || 'Failed to delete credit',
          status: response.status,
        };
      },
    }),

    // Get credit details for specific user
    getUserCredit: build.query<CreditApiResponse<any>, { user_id: number }>({
      query: ({ user_id }) => {
        const formData = {
          user_id: user_id,
        };
        
        console.log('GetUserCredit request data:', formData);
        
        return {
          url: CREDIT_API_CONFIG.ENDPOINTS.GET_USER_CREDIT,
          method: 'POST',
          body: formData,
        };
      },
      providesTags: ['UserCredit'],
      transformResponse: (response: any) => {
        console.log('GetUserCredit response:', response);
        return {
          error: response.error || false,
          message: response.message || '',
          result: response.result || response.data,
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('GetUserCredit error:', response);
        return {
          error: true,
          message: response.data?.message || response.error || 'Failed to get user credit',
          status: response.status,
        };
      },
    }),

    // Search users for credit assignment
    searchUsers: build.query<CreditApiResponse<SearchUser[]>, SearchUserRequest>({
      query: (params) => {
        const requestData = {
          search: params.search || '',
          limit: params.limit || 20,
        };
        
        console.log('SearchUsers request data:', requestData);
        
        return {
          url: CREDIT_API_CONFIG.ENDPOINTS.SEARCH_USERS,
          method: 'POST',
          body: requestData,
        };
      },
      providesTags: ['SearchUsers'],
      transformResponse: (response: any) => {
        console.log('SearchUsers raw response:', response);
        
        let usersData: SearchUser[] = [];
        
        if (response?.data && Array.isArray(response.data)) {
          usersData = response.data.map((user: any) => ({
            user_id: user.id || user.user_id,
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            email: user.email || user.user_email || '',
            phone: user.phone || user.user_contact || '',
          }));
        }
        
        return {
          error: response?.error || false,
          message: response?.message || '',
          result: usersData,
          data: usersData,
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('SearchUsers error:', response);
        return {
          error: true,
          message: response.data?.message || response.error || 'Failed to search users',
          status: response.status,
        };
      },
    }),
  }),
});

export const {
  useSetCreditMutation,
  useListCreditUsersQuery,
  useListAllUsersQuery,
  useUpdateCreditMutation,
  useDeleteCreditMutation,
  useGetUserCreditQuery,
  useSearchUsersQuery, 
} = creditSalesApi;
